import type { ParsedQs } from 'qs'
import qs from 'qs'
import type { UiState } from 'instantsearch.js'
import type { AlgoliaSearchedProductsLocalStorage } from 'algolia/algolia'
import {
  algoliaProductIndex,
  algoliaProductsIndexNewest,
  algoliaProductsIndexOldest,
} from 'algolia/algolia'
import { localStore } from 'utils/storage'

const indexToQuery: Record<string, string> = {
  [algoliaProductIndex]: '',
  [algoliaProductsIndexNewest]: 'newest',
  [algoliaProductsIndexOldest]: 'oldest',
}

const queryToIndex: Record<string, string> = {
  newest: algoliaProductsIndexNewest,
  oldest: algoliaProductsIndexOldest,
}

export const getSearchState = ({ url }: { url: string }) => {
  const queryObject = qs.parse(url)

  return queryToSearchState(queryObject)
}

export interface QueryToSearchState {
  uiState: UiState
  other?: {
    filter?: ParsedQsValue
    aiContentType?: ParsedQsValue
  }
}

export const queryToSearchState = (queryObject: ParsedQs): QueryToSearchState => {
  const { query = '', page, sortBy, category, type, filter, aiContentType } = queryObject

  const state: UiState = {
    [algoliaProductIndex]: {
      query: decodeURIComponent(String(query)),
      // oxlint-disable-next-line no-negated-condition
      page: !Number(page) ? 1 : Number(page),
      sortBy: sortBy ? queryToIndex[decodeURIComponent(String(sortBy))] || '' : '',
      refinementList: {
        category: decodeQueryArray(category),
        type: decodeQueryArray(type),
      },
    },
  }

  return {
    uiState: state,
    other: {
      filter: decodeQueryArray(filter),
      aiContentType: decodeQueryArray(aiContentType),
    },
  }
}

export const searchStateToQuery = (searchState: QueryToSearchState) => {
  const { query, page, refinementList, sortBy } = searchState.uiState[algoliaProductIndex]
  const { category, type } = refinementList || {}

  const filter = searchState.other?.filter
  const aiContentType = searchState.other?.aiContentType

  const queryObject = {
    filter: filter ? decodeQueryArray(filter) : undefined,
    query: query ? query : undefined,
    sortBy: sortBy ? indexToQuery[sortBy] : undefined,
    page: page && page !== 1 ? page : undefined,
    category: category ? category.map(encodeURIComponent) : [],
    aiContentType: aiContentType ? decodeQueryArray(aiContentType) : undefined,
    type: type ? type.map(encodeURIComponent) : [],
  }

  return qs.stringify(queryObject, { arrayFormat: 'comma' })
}

type ParsedQsValue = ParsedQs[keyof ParsedQs]

export const decodeQueryArray = (value: ParsedQsValue): string[] => {
  if (typeof value === 'string') {
    if (value.length === 0) {
      return []
    }

    // oxlint-disable-next-line unicorn/prefer-includes
    if (value.indexOf(',') > -1) {
      return value.split(',').map(decodeURIComponent)
    }

    return [decodeURIComponent(value)]
  }

  if (value && Array.isArray(value)) {
    const stringValues: string[] = []

    value.forEach((item: ParsedQsValue) => {
      if (typeof item === 'string') {
        stringValues.push(item)
      }
    })

    return stringValues.map(decodeURIComponent)
  }

  return []
}

export const getLocalStorageSearchedProducts = (
  userId?: string,
): AlgoliaSearchedProductsLocalStorage | null => {
  try {
    return JSON.parse(
      localStore.getString(`algoliaSearchedProducts.${userId || 'guest'}`) || 'null',
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing algoliaSearchedProducts from localStorage', error)
    return null
  }
}

export const saveLocalStorageSearchedProducts = (
  data: AlgoliaSearchedProductsLocalStorage,
  userId?: string,
) => {
  localStore.setJSON(`algoliaSearchedProducts.${userId || 'guest'}`, data)
}

export function isSearchPage(pathname: string) {
  return pathname.includes('/search/')
}
